import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch, Prop, Emit, Ref } from 'vue-property-decorator';
import { CLASSES_STORE } from '@/classes/store/classes.store';
import { MEETINGS_STORE } from '@/meetings/store/meetings.store';
import EdListClassStudentsComponent from '../ed-list-class-students/ed-list-class-students.component.vue';
import EdListClassTeachersComponent from '../ed-list-class-teachers/ed-list-class-teachers.component.vue';
import EdListClassCoursesComponent from '../ed-list-class-courses/ed-list-class-courses.component.vue';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import EdInputModel from '@/models/ed-input.model';
import {maxLength} from 'vuelidate/lib/validators';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import EdEditDialog from '@/components/common/ed-edit-dialog/ed-edit-dialog.component.vue';
import { dataTableResponsiveBreakpoint } from '@/constants/constants-components';
import BrowserService from '@/providers/browser-utils/browser.service';
import { showCustomTooltip } from '@/utils/wc-data-table-utils';
import Moment from 'moment';

@Component({
  name: 'ed-list-classes',
  components: {
    EdListClassStudents: EdListClassStudentsComponent,
    EdListClassTeachers: EdListClassTeachersComponent,
    EdListClassCourses: EdListClassCoursesComponent
  }
})
export default class EdListClassesComponent extends Vue {

  /* Props */
  @Prop() readonly department: any;
  @Prop() readonly orgId: number;
  @Prop() readonly sortBy: string;

  /* Refs */
  @Ref('dataTable') readonly dataTable: any;
  @Ref('expandedList') readonly expandedList: any;

  /* Data */
  public openedColumn: string = ''
  public expanded: any = [];
  public prevExpanded: any = [];
  public loading: boolean = false;
  public pagination: object = {
    current: 1,
    total: 0
  }
  public selection: any = {};
  public className = EdInputModel('className',  '', {maxLength: maxLength(60)});
  private classItem: any = null;
  private isClassItemUpdate: boolean = false;

  public checkboxGroup = {};
  public checkboxItem = {};

  private isMobileView: boolean = false;
  public groupSelectionState = {
    NONE: 'NONE',
    SOME: 'SOME',
    ALL: 'ALL',
  }

  /*Watchers*/
  @Watch('getClasses')
  getModel(items: any) {
    if (!Array.isArray(items)) {
      return;
    }

    const tableSelection = (this.$refs.table as any)?.selection;
    if (!tableSelection) {
      return;
    }

    items.forEach((item) => {
      const isSelected = Boolean(tableSelection[item.id]);
      this.checkboxItem[item.id] = EdInputModel(`${item.id}`, isSelected);
      this.checkboxGroup[item.teacher.id] = EdInputModel(`${item.teacher.id}`, false);
    })
  }

  selectItem(item: any) {
    const table = this.$refs.table as any;

    if (!table || !table.selection) {
      return;
    }

    const itemSelected = table.selection;
    const isSelected = !(item.id in itemSelected);

    Vue.set(this.checkboxItem, item.id, EdInputModel(`${item.id}`, isSelected));
    Vue.set(table.selection, item.id, isSelected ? item : null);

    if (!isSelected) {
      delete table.selection[item.id];
    }
  }

  /* Computed */
  get classItemName() {
    return this.isClassItemUpdate ? this.className.value : this.classItem.name;
  }

  get isMassEditEnabled() {
    if (!this.selection) {
      return false
    }
    const selectionArray = Object.entries(this.selection) as unknown as [number, any][];

    if (selectionArray.length === 0) {
      return false
    }

    const scheduleHash = selectionArray[0][1].scheduleHash;
    const areSameHashSchedule = selectionArray.every(([key, value]) => value.scheduleHash === scheduleHash && Boolean(selectionArray[0][1].schoolDay));

    if (areSameHashSchedule) {
      return true
    }

    return false;
  }

  get writeClassPriv() {
    return this.$store.state.AuthStore.privileges.WriteClass;
  }

  get isStudent() {
    return this.$store.state.AuthStore.user.role === 'STUDENT';
  }

  get  headers() {
    return [
      { text: this.$t('classes.className'), align: 'start', value: 'name', show: true },
      { text: this.$t('classes.headers.teachers'), value: 'teacher', sortable: false, show: true },
      { text: this.$t('classes.headers.courseAccess'), value: 'allCoursesAccess', sortable: false, show: this.writeClassPriv },
      { text: this.$t('classes.headers.students'), value: 'students', sortable: false, show: true },
      { text: this.$t('classes.headers.courses'), value: 'courses', sortable: false, show: true },
      { text: this.$t('classes.headers.schedule'), value: 'schedule', sortable: false, show: !this.isStudent },
      { text: this.$t('classes.headers.classBuilder'), value: 'classBuilder', sortable: false, show: !this.isStudent },
      { text: this.$t('classes.headers.actions'), value: 'actions', sortable: false, show: this.writeClassPriv }
    ].filter(el => el.show)
  }

  get pagedClasses() {
    return this.$store.state.ClassesStore.pagedClasses;
  }

  get max60Char() {
    return v => v.length <= 60 || this.$t('errors.maxLength');
  }

  get getClasses() {
    this.loading = true;

    // Check with store if the page was loaded previously
    let classes = this.pagedClasses.find(el => el.page === this.pagination['current']-1)?.content;

    // Fetch new page
    if (!classes && this.department?.id) {
      this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_CLASSES_BY_DEPARTMENT_A}`,{
        departmentId: this.department.id, page: this.pagination['current']-1,
        sortBy: this.sortBy,
        pageSize: 10
      }).then((res) => {
        this.pagination['total'] = res.totalPages;
        this.loading = false;
        return res
      }).catch((err) => {
        this.loading = false;
      });
    } else {
      this.loading = false;
    }

    return classes;
  }

  get groupBy() {
    if(this.sortBy === 'CLASS') return null
    return this.sortBy === 'TEACHER' ? 'teacher.fullName' : 'grade.name'
  }

  get expandedItem(): any {
    return this.expanded?.length > 0 ? this.expanded[0] : undefined;
  }

  @Emit('onSchedulesEdit')
  emitOnSchedulesEdit(classItems) {
    return classItems;
  }

  @Emit('onScheduleCreate')
  emitOnScheduleCreate(classItem) {
    return classItem;
  }

  @Emit('onGoToClassBuilder')
  emitOnGoToClassBuilder(classItem) {
    return classItem;
  }

  /* Methods */
  clickColumn(slotData: any) {
    this.openedColumn = slotData.header.value;
    if (this.prevExpanded.header &&
      (slotData.header.value === this.prevExpanded.header.value || this.prevExpanded.header.value === 'allCoursesAccess') &&
      slotData?.item?.id === this.prevExpanded?.item?.id) {
      this._clearExpandedItem();
    } else {
      this.expanded = [slotData.item]
      this.prevExpanded = slotData;
    }
    this._loadDataTable();
  }

  onItemSelected(customEvent: CustomEvent) {
    if (customEvent?.detail) {
      this.selection = {};
      customEvent.detail.selectedItems?.forEach((item: any) => {
        this.selection[item.id] = item?.value?.raw;
      });
    }
  }

  getCheckboxGroupKey(items: any) {
    if (!Array.isArray(items)) {
      return;
    }

    const tableSelection = (this.$refs.table as any)?.selection;
    if (!tableSelection) {
      return;
    }

    const allSelected = items.every(item => tableSelection[item.id]);
    Vue.set(this.checkboxGroup, items[0].teacher.id, EdInputModel(`${items[0].teacher.id}`, allSelected));

    items.forEach(item => {
      Vue.set(this.checkboxItem, item.id, EdInputModel(`${item.id}`, allSelected));
    });

    return items[0].teacher.id;
  }

  getCheckboxItemKey(item: any) {
    const tableSelection = (this.$refs.table as any)?.selection;
    if (!tableSelection) {
      return;
    }

    const isSelected = (item.id in tableSelection);
    Vue.set(this.checkboxItem, item.id, EdInputModel(`${item.id}`, isSelected));

    return item.id;
  }

  public isSelectedGroup(items: any[]) {
    return this.getGroupSelectionState(items) === this.groupSelectionState.ALL;
  }

  public getGroupSelectionState(items: any[]): string {
    const localItems: number[] = items.map(item => item?.raw?.id);
    const selection: number[] = this.dataTable?.selectedItems
      .filter(item => item?.value?.type == 'item')
      .map(item => item?.value?.raw?.id);

    if (localItems.every(item => selection.includes(item))) {
      return this.groupSelectionState.ALL;
    
    } else if (localItems.some(item => selection.includes(item))) {
      return this.groupSelectionState.SOME;

    } else {
      return this.groupSelectionState.NONE;
    }
  }

  async updateClass(classItem) {
    let body = {
      'name': classItem.name,
      'departmentId': classItem.department.id,
      'teacherId': classItem.teacher.id,
      'visibility': classItem.visibility,
      'enrollment': classItem.enrollment,
      'capacity': classItem.capacity,
      'allCoursesAccess': classItem.allCoursesAccess,
      'gradeLevelId': classItem?.grade?.id,
    }
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.UPDATE_CLASS_A}`, {classId: classItem.id, body}).then((res) => {
      this.$store.commit(MEETINGS_STORE.MUTATIONS.UPDATE_CLASS_NAME_M, {classId: classItem.id, className: classItem.name });
      this.$EdToast.create({
        msg:  `${this.$t('classes.success.classUpdated')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch(() => {
      this.$EdToast.create({
        msg:  `${this.$t('classes.errors.classNoUpdated')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })

    this.isClassItemUpdate = true;
  }

  deleteItem (item) {
    this.$EdModal.createConfirmationModal(
      {
        body: this.$t('classes.confirmRemoveClass',
          {
            className: item.name
          }),
        action: async () => {
          await this.deleteItemConfirm(item);
        }
      }
    );
  }

  inactivateItem (item) {
    this.$EdModal.createConfirmationModal(
      {
        body: this.$t('classes.confirmInactivateClass',
          {
            className: item.name
          }),
        action: async () => {
          await this.inactivateItemConfirm(item);
        }
      }
    );
  }

  deleteItemConfirm (item) {
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.DELETE_CLASS_A}`, item.id).then((res) => {
      this.$EdToast.create({
        msg:  `${this.$t('classes.success.classDeleted')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      this._clearSelectedItems();
    }).catch(() => {
      this.$EdToast.create({
        msg:  `${this.$t('classes.errors.classNoDeleted')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })
  }

  inactivateItemConfirm (item) {
    this.$store.dispatch(`${CLASSES_STORE.ACTIONS.INACTIVATE_CLASS_A}`, item.id).then((res) => {
      this.$EdToast.create({
        msg:  `${this.$t('classes.success.classInactivated')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
      this._clearSelectedItems();
    }).catch(() => {
      this.$EdToast.create({
        msg:  `${this.$t('classes.errors.classNoInactivated')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })  
  }

  grantCourseAccess(slotData: any): void {
    if(this.isCalendarExpired(slotData.item))
      return;
    if (this.writeClassPriv) {
      this.updateCourseAccessConfirm(slotData);
    }
  }

  updateCourseAccessConfirm(slotData) {
    let allCoursesAccess = slotData.item.allCoursesAccess ? 'False' : 'True';
    this.$EdModal.createConfirmationModal(
      {
        body: this.$t(`classes.info.confirmCourseAccess${allCoursesAccess}`),
        action: async () => {
          slotData.item.allCoursesAccess = !slotData.item.allCoursesAccess
          await this.updateClass(slotData.item);
          this.dataTable?.updateItem(slotData.item);

          if (!slotData.item.allCoursesAccess) {
            this.openedColumn = 'courses'
            this.expanded = [slotData.item]
            this.prevExpanded = slotData;
            this._scheduleLoadDataTable();
          }
        }
      }
    );
  }

  created() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }

  beforeDestroy() {
    this.$store.commit(CLASSES_STORE.MUTATIONS.CLEAR_CLASSES_M);
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }

  selectGroup(items: any, isChecked: boolean) {
    items.forEach((item) => {
      if (isChecked) {
        delete this.selection[item?.raw?.id];
      } else {
        this.selection[item?.raw?.id] = item?.raw;
      }
    });
    this.dataTable?.selectGroup(items);
  }

  public isExpanded(header: string, itemId: number): boolean {
    return this.expanded?.length > 0 && header === this.openedColumn && itemId === this.expanded[0].id;
  }

  public isCalendarExpired(item) {
    return Moment(item.schoolDay?.endDate).isBefore(Moment());
  }

  public onPageUpdate(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    this.pagination['current'] = detail.page;
    this._loadDataTable();
  }

  public editClassName(item: any): void {
    this.$EdModal.createModal(EdEditDialog, {
      inputText: item?.name, onChangeCallback: async (newClassName: string) => {
        item.name = newClassName;
        await this.updateClass(item);
        this.dataTable?.updateItem(item);
      },
      required: true
    });
  }

  private _clearSelectedItems(): void {
    this.selection = {};
    this.dataTable?.clearSelectedItems();
  }

  private _scheduleLoadDataTable(): void {
    this.$nextTick(() => {
      this._loadDataTable();
    });
  }

  private _onLanguageChanged(lang: string): void {
    this.dataTable?.setAttribute('lang', lang);
    this._scheduleLoadDataTable();
  }

  private _loadDataTable(): void {

    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.getClasses || this.getClasses.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.data-table-expand': { template: '' },
      'group.header': {
        template: document.querySelector('#classes\\.group\\.header').innerHTML,
        templateParams:[{key:'group.name'}],
        expressions: [
          {
            target: '.group-none-selected', attribute: 'style', booleanAttribute: false, concat: true,
            expression: (params) => params.parent.getGroupSelectionState(params.group.items) === params.parent.groupSelectionState.NONE,
            applied: 'display: inline;', fallback: 'display: none;',
          },
          {
            target: '.group-some-selected', attribute: 'style', booleanAttribute: false, concat: true,
            expression: (params) => params.parent.getGroupSelectionState(params.group.items) === params.parent.groupSelectionState.SOME,
            applied: 'display: inline;', fallback: 'display: none;',
          },
          {
            target: '.group-all-selected', attribute: 'style', booleanAttribute: false, concat: true,
            expression: (params) => params.parent.getGroupSelectionState(params.group.items) === params.parent.groupSelectionState.ALL,
            applied: 'display: inline;', fallback: 'display: none;',
          },
        ],
        events: [
          { target: '.select-group', handler: (params) => params.parent.selectGroup(params.group.items, params.parent.isSelectedGroup(params.group.items)) },
        ],
      },
      'item.name': {
        template: document.querySelector('#classes\\.item\\.name').innerHTML,
        templateParams: [
          { key: 'item.name', value: (params) => params.item.name},
        ],
        expressions: [
          {
            target: '.editable-class-name', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.writeClassPriv,
            applied: 'display: inline;', fallback: 'display: none;',
          },
          {
            target: '.uneditable-class-name', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.writeClassPriv,
            applied: 'display: inline;', fallback: 'display: none;',
          },
        ],
        customTooltip: [
          {
            key: 'item.name',
            show: showCustomTooltip(this, (params: any) => params.item.name),
          }
        ],
        events: [
          { target: '.edit-name', handler: (params) => params.parent.editClassName(params.item) },
        ],
      },
      'item.teacher': {
        template: document.querySelector('#classes\\.item\\.teacher').innerHTML,
        templateParams: [
          { key: 'item.teacher.fullName', value: (params) => params.item.teacher.fullName},
        ],
        expressions: [
          {
            target: '.show-teachers', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.isExpanded(params.slotData.header.value, params.item.id),
            applied: 'display: inline; pointer-events: none;', fallback: 'display: none;',
          },
          {
            target: '.hide-teachers', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.isExpanded(params.slotData.header.value, params.item.id),
            applied: 'display: inline; pointer-events: none;', fallback: 'display: none;',
          },
        ],
        events: [
          { target: '.teachers', handler: (params) => params.parent.clickColumn(params.slotData) },
        ],
      },
      'item.allCoursesAccess': {
        template: document.querySelector('#classes\\.item\\.allCoursesAccess').innerHTML,
        customTooltip: [
          {
            key: 'item.allCoursesAccessTitle',
            show: showCustomTooltip(this, (params: any) => params.item.allCoursesAccess ?
              params.parent.$t('classes.info.infoCourseAccessFalse')
              : params.parent.$t('classes.info.infoCourseAccessTrue')
            ),
          }
        ],
        expressions: [
          {
            target: 'input[type="checkbox"]', attribute: 'checked', booleanAttribute: true,
            expression: (params) => params.item.allCoursesAccess,
            applied: 'true', fallback: 'false',
          },
        ],
        events: [
          { target: '.grant-access', handler: (params) => params.parent.grantCourseAccess(params.slotData), }
        ],
      },
      'item.students': {
        template: document.querySelector('#classes\\.item\\.students').innerHTML,
        expressions: [
          {
            target: '.show-students', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.isExpanded(params.slotData.header.value, params.item.id),
            applied: 'display: inline; pointer-events: none;', fallback: 'display: none;',
          },
          {
            target: '.hide-students', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.isExpanded(params.slotData.header.value, params.item.id),
            applied: 'display: inline; pointer-events: none;', fallback: 'display: none;',
          },
        ],
        events: [
          { target: '.students', handler: (params) => params.parent.clickColumn(params.slotData) },
        ],
      },
      'item.courses': {
        template: document.querySelector('#classes\\.item\\.courses').innerHTML,
        expressions: [
          {
            target: '.show-courses', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.parent.isExpanded(params.slotData.header.value, params.item.id),
            applied: 'display: inline; pointer-events: none;', fallback: 'display: none;',
          },
          {
            target: '.hide-courses', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.isExpanded(params.slotData.header.value, params.item.id),
            applied: 'display: inline; pointer-events: none;', fallback: 'display: none;',
          },
        ],
        events: [
          { target: '.courses', handler: (params) => params.parent.clickColumn(params.slotData) },
        ],
      },
      'item.schedule': {
        template: document.querySelector('#classes\\.item\\.schedule').innerHTML,
        expressions: [
          {
            target: '.edit-schedule', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.hasSchedule || !!params.item.scheduleType,
            applied: 'display: inline;', fallback: 'display: none;',
          },
          {
            target: '.edit-schedule', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => !params.item.schoolDay,
            applied: 'true', fallback: 'false',
          },
          {
            target: '.create-schedule', attribute: 'style', booleanAttribute: false,
            expression: (params) => !params.item.hasSchedule && !params.item.scheduleType,
            applied: 'display: inline;', fallback: 'display: none;',
          },
          {
            target: '.create-schedule', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => !params.item.schoolDay,
            applied: 'true', fallback: 'false',
          },
        ],
        events: [
          { target: '.edit-schedule', handler: (params) => params.parent.emitOnSchedulesEdit(params.item) },
          { target: '.create-schedule', handler: (params) => params.parent.emitOnScheduleCreate(params.item) },
        ],
      },
      'item.classBuilder': {
        template: document.querySelector('#classes\\.item\\.classBuilder').innerHTML,
        expressions: [
          {
            target: '.class-builder', attribute: 'disabled', booleanAttribute: true,
            expression: (params) => !params.item.hasSchedule,
            applied: 'true', fallback: 'false',
          },
        ],
        events: [
          { target: '.class-builder', handler: (params) => params.parent.emitOnGoToClassBuilder(params.item) }
        ],
      },
      'item.actions': {
        template: document.querySelector('#classes\\.item\\.actions').innerHTML,
        expressions: [
          {
            target: '.inactivate-action', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.parent.isCalendarExpired(params.item),
            applied: 'display: inline;', fallback: 'display: none;', concat: true
          }
        ],
        events: [
          { target: '.remove-action', handler: (params) => params.parent.deleteItem(params.item) },
          { target: '.inactivate-action', handler: (params) => params.parent.inactivateItem(params.item) }
        ],
      },
      parent: this,
    };

    this.dataTable.loadData({
      headers: this.headers,
      items: this.getClasses,
      totalItems: this.getClasses.length,
      groupBy: this.groupBy ? [this.groupBy] : [],
      expanded: this.expanded,
      slots: slots,
    });
  }

  onDataTableResize(): void {

    // It ensures Safari takes the scroll position after tab change
    if (BrowserService.isSafari()) {
      const scrollContainer: HTMLElement = this.dataTable.shadowRoot?.querySelector('.data-table__wrapper');
      const scrollLeft: number = scrollContainer?.scrollLeft ?? 0;
      scrollContainer?.scrollTo({left: 0});
      scrollContainer?.scrollTo({left: scrollLeft});
    }

    if (!this.openedColumn) {
      return;
    }

    const offsetWidth: number = (this.dataTable as HTMLElement)?.offsetWidth ?? 0;
    if (offsetWidth === 0) {
      return;
    }

    const mobileView: boolean = offsetWidth < dataTableResponsiveBreakpoint;
    if (this.isMobileView === mobileView) {
      return;
    }

    this.isMobileView = mobileView;

    if (this.expandedList?.$el && this.expandedList?.refreshDataTable) {
      this.expandedList.$el.style = 'width: 1%; heigth: 1%;';
      setTimeout(() => {
        this.expandedList.$el.style = '';
        this.expandedList.refreshDataTable();
      }, 100);
    }
  }

  // Watch
  @Watch('sortBy')
  onSortByChanged() {
    this.$store.commit(CLASSES_STORE.MUTATIONS.CLEAR_CLASSES_M);
  }

  @Watch('sortBy', { deep: true, immediate: true })
  private _watchSortBy(): void {
    this._clearExpandedItem();
  }

  @Watch('getClasses', { immediate: true })
  private _watchGetClasses(): void {
    if (this.expandedItem && this.getClasses?.length > 0) {
      const item: any = this.getClasses.find((item) => item.id === this.expandedItem.id);
      if (item) {
        this.expanded = [item];
      } else {
        this._clearExpandedItem();
      }
    }
    this._loadDataTable();
  }

  private _clearExpandedItem(): void {
    this.expanded = []
    this.prevExpanded = {};
    this.openedColumn = '';
  }

  loadData(item): void {
    this.classItem = item;
    this.isClassItemUpdate = false;
    this.className.value = item.name || '';
  }

  mounted() {
    this.dataTable.loadData({ items: [], headers: this.headers });
  }
}